import { useQuery, keepPreviousData } from "@tanstack/react-query"
import userService from "../services/user.service"
import serviceService from "../services/service.service"
import clientService from "../services/client.service"
import commentService from "../services/comment.service"
import recordService from "../services/record.service"
import companyService from "../services/company.service"
import localStorageService from "../services/localStorage.service"
import { getSalonSlug } from "../utils/salonHost"

export const QUERY_STALE_MS = 5 * 60 * 1000

const USERS_LIMIT = 2000
const USERS_OFFSET = 0
export const CLIENTS_LOAD_LIMIT = 3000
export const COMMENTS_LOAD_LIMIT = 2000
export const RECORDS_LOAD_LIMIT = 2000

function clientCompanyQueryPrefix() {
  const slug = getSalonSlug()
  return slug ? ["company", slug] : []
}

export function pickUserProfile(user) {
  if (!user) return null
  return {
    id: user.id,
    isCalendarAdmin: !!user.isCalendarAdmin,
    isStaff: !!user.isStaff,
    isCompanyOwner: !!user.isCompanyOwner,
    isPlatformAdmin: !!user.isPlatformAdmin,
    companyId: user.companyId ?? null,
    companyActive: user.companyActive ?? null,
    name: user.name,
  }
}

export function pickClientProfile(client) {
  if (!client) return null
  return {
    id: client.id,
    name: client.name,
    phone: client.phone,
  }
}

export function useUsers({
  limit = USERS_LIMIT,
  offset = USERS_OFFSET,
  enabled = true,
  withTotal = false,
  qId,
  qName,
  qPhone,
  qPosition,
  qRole,
  birthdateFrom,
  birthdateTo,
  employee,
  sort,
  sortDir,
} = {}) {
  return useQuery({
    queryKey: [
      ...clientCompanyQueryPrefix(),
      "users",
      limit,
      offset,
      withTotal,
      qId || "",
      qName || "",
      qPhone || "",
      qPosition || "",
      qRole || "",
      birthdateFrom || "",
      birthdateTo || "",
      employee || "",
      sort || "",
      sortDir || "",
    ],
    queryFn: () =>
      userService.getUsers({
        limit,
        offset,
        withTotal,
        qId,
        qName,
        qPhone,
        qPosition,
        qRole,
        birthdateFrom,
        birthdateTo,
        employee,
        sort,
        sortDir,
      }),
    staleTime: QUERY_STALE_MS,
    enabled,
    placeholderData: withTotal ? keepPreviousData : undefined,
  })
}

export function useServices() {
  return useQuery({
    queryKey: [...clientCompanyQueryPrefix(), "services"],
    queryFn: () => serviceService.getServices(),
    staleTime: QUERY_STALE_MS,
  })
}

export function useServicesManagement({
  limit,
  offset,
  withTotal = false,
  qId,
  qName,
  qPrice,
  qDuration,
  qActive,
  qComplexes,
  sort,
  sortDir,
} = {}) {
  return useQuery({
    queryKey: [
      "services-management",
      limit ?? "",
      offset ?? "",
      withTotal,
      qId || "",
      qName || "",
      qPrice || "",
      qDuration || "",
      qActive || "",
      qComplexes || "",
      sort || "",
      sortDir || "",
    ],
    queryFn: () =>
      serviceService.getServicesManagement({
        limit,
        offset,
        withTotal,
        qId,
        qName,
        qPrice,
        qDuration,
        qActive,
        qComplexes,
        sort,
        sortDir,
      }),
    staleTime: QUERY_STALE_MS,
    placeholderData: withTotal ? keepPreviousData : undefined,
  })
}

export function useCurrentService(serviceId) {
  return useQuery({
    queryKey: ["service", serviceId],
    queryFn: () => serviceService.getServiceById(serviceId),
    enabled: Boolean(serviceId),
    staleTime: QUERY_STALE_MS,
  })
}

export function useComplexes() {
  return useQuery({
    queryKey: [...clientCompanyQueryPrefix(), "complexes"],
    queryFn: () => serviceService.getComplexes(),
    staleTime: QUERY_STALE_MS,
  })
}

export function useComplexesManagement({
  limit,
  offset,
  withTotal = false,
  qId,
  qName,
  sort,
  sortDir,
} = {}) {
  return useQuery({
    queryKey: [
      "complexes-management",
      limit ?? "",
      offset ?? "",
      withTotal,
      qId || "",
      qName || "",
      sort || "",
      sortDir || "",
    ],
    queryFn: () =>
      serviceService.getComplexesManagement({
        limit,
        offset,
        withTotal,
        qId,
        qName,
        sort,
        sortDir,
      }),
    staleTime: QUERY_STALE_MS,
    placeholderData: withTotal ? keepPreviousData : undefined,
  })
}

export function useCompanies({
  enabled = true,
  limit,
  offset,
  withTotal = false,
  qId,
  qName,
  qSlug,
  qActive,
  qOwner,
  qCreated,
  createdFrom,
  createdTo,
  sort,
  sortDir,
} = {}) {
  return useQuery({
    queryKey: [
      "companies",
      limit ?? "",
      offset ?? "",
      withTotal,
      qId || "",
      qName || "",
      qSlug || "",
      qActive || "",
      qOwner || "",
      qCreated || "",
      createdFrom || "",
      createdTo || "",
      sort || "",
      sortDir || "",
    ],
    queryFn: () =>
      companyService.listCompanies({
        limit,
        offset,
        withTotal,
        qId,
        qName,
        qSlug,
        qActive,
        qOwner,
        qCreated,
        createdFrom,
        createdTo,
        sort,
        sortDir,
      }),
    staleTime: QUERY_STALE_MS,
    enabled,
    placeholderData: withTotal ? keepPreviousData : undefined,
  })
}

export function useCurrentComplex(complexId) {
  return useQuery({
    queryKey: ["complex", complexId],
    queryFn: () => serviceService.getComplexById(complexId),
    enabled: Boolean(complexId),
    staleTime: QUERY_STALE_MS,
  })
}

export function useCurrentUser(userId) {
  return useQuery({
    queryKey: ["user", userId],
    queryFn: () => userService.getUserById(userId),
    enabled: Boolean(userId),
    staleTime: QUERY_STALE_MS,
  })
}

export function useCurrentClient(clientId) {
  return useQuery({
    queryKey: [...clientCompanyQueryPrefix(), "client", clientId],
    queryFn: () => clientService.getClientById(clientId),
    enabled: Boolean(clientId),
    staleTime: QUERY_STALE_MS,
  })
}

export function useClients({
  limit = CLIENTS_LOAD_LIMIT,
  offset = 0,
  withTotal = false,
  qId,
  qName,
  qPhone,
  qBirthdate,
  birthdateFrom,
  birthdateTo,
  qLastRecordId,
  qLastRecordDate,
  lastRecordDateFrom,
  lastRecordDateTo,
  qLastRecordStatus,
  qTotalRecords,
  qLastService,
  sort,
  sortDir,
} = {}) {
  return useQuery({
    queryKey: [
      "clients",
      limit,
      offset,
      withTotal,
      qId || "",
      qName || "",
      qPhone || "",
      qBirthdate || "",
      birthdateFrom || "",
      birthdateTo || "",
      qLastRecordId || "",
      qLastRecordDate || "",
      lastRecordDateFrom || "",
      lastRecordDateTo || "",
      qLastRecordStatus || "",
      qTotalRecords || "",
      qLastService || "",
      sort || "",
      sortDir || "",
    ],
    queryFn: () =>
      clientService.getClients({
        limit,
        offset,
        withTotal,
        qId,
        qName,
        qPhone,
        qBirthdate,
        birthdateFrom,
        birthdateTo,
        qLastRecordId,
        qLastRecordDate,
        lastRecordDateFrom,
        lastRecordDateTo,
        qLastRecordStatus,
        qTotalRecords,
        qLastService,
        sort,
        sortDir,
      }),
    staleTime: QUERY_STALE_MS,
    placeholderData: withTotal ? keepPreviousData : undefined,
  })
}

export function useComments({ limit = COMMENTS_LOAD_LIMIT, offset = 0 } = {}) {
  return useQuery({
    queryKey: ["comments", limit, offset],
    queryFn: () => commentService.getComments({ limit, offset }),
    staleTime: QUERY_STALE_MS,
  })
}

export function useCommentsManagement({
  limit = COMMENTS_LOAD_LIMIT,
  offset = 0,
  withTotal = false,
  qId,
  qRating,
  qAuthor,
  qAuthorStatus,
  qRecordId,
  qRecordService,
  qRecordMaster,
  qRecordDatetime,
  recordDateFrom,
  recordDateTo,
  createdFrom,
  createdTo,
  scope,
  sort,
  sortDir,
} = {}) {
  return useQuery({
    queryKey: [
      "comments-management",
      limit,
      offset,
      withTotal,
      qId || "",
      qRating || "",
      qAuthor || "",
      qAuthorStatus || "",
      qRecordId || "",
      qRecordService || "",
      qRecordMaster || "",
      qRecordDatetime || "",
      recordDateFrom || "",
      recordDateTo || "",
      createdFrom || "",
      createdTo || "",
      scope || "",
      sort || "",
      sortDir || "",
    ],
    queryFn: () =>
      commentService.getCommentsManagement({
        limit,
        offset,
        withTotal,
        qId,
        qRating,
        qAuthor,
        qAuthorStatus,
        qRecordId,
        qRecordService,
        qRecordMaster,
        qRecordDatetime,
        recordDateFrom,
        recordDateTo,
        createdFrom,
        createdTo,
        scope,
        sort,
        sortDir,
      }),
    staleTime: QUERY_STALE_MS,
    placeholderData: withTotal ? keepPreviousData : undefined,
  })
}

export function useCurrentComment(commentId) {
  return useQuery({
    queryKey: ["comment", commentId],
    queryFn: () => commentService.getCommentById(commentId),
    enabled: Boolean(commentId),
    staleTime: QUERY_STALE_MS,
  })
}

export function useUserRecords(userId, { limit = RECORDS_LOAD_LIMIT } = {}) {
  return useQuery({
    queryKey: ["records", "user", userId, limit],
    queryFn: () =>
      recordService.getRecords({ userId: Number(userId), limit }),
    enabled: Boolean(userId),
    staleTime: QUERY_STALE_MS,
  })
}

export function useClientRecords(clientId, date) {
  return useQuery({
    queryKey: [...clientCompanyQueryPrefix(), "records", "client", clientId, date],
    queryFn: () =>
      recordService.getRecords({
        clientId: Number(clientId),
        date,
      }),
    enabled: Boolean(clientId) && Boolean(date),
    staleTime: QUERY_STALE_MS,
  })
}

export function useRecordsManagement({
  limit = RECORDS_LOAD_LIMIT,
  offset = 0,
  dateFrom,
  dateTo,
  status,
  qId,
  qService,
  qClient,
  qMaster,
  qAuthor,
  qTime,
  qStatus,
  qPrice,
  qForecast,
  qCreated,
  createdFrom,
  createdTo,
  withTotal = false,
  sort,
  sortDir,
} = {}) {
  return useQuery({
    queryKey: [
      "records-management",
      limit,
      offset,
      dateFrom || "",
      dateTo || "",
      status || "",
      qId || "",
      qService || "",
      qClient || "",
      qMaster || "",
      qAuthor || "",
      qTime || "",
      qStatus || "",
      qPrice || "",
      qForecast || "",
      qCreated || "",
      createdFrom || "",
      createdTo || "",
      withTotal,
      sort || "",
      sortDir || "",
    ],
    queryFn: () =>
      recordService.getRecords({
        limit,
        offset,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        status: status || undefined,
        qId: qId || undefined,
        qService: qService || undefined,
        qClient: qClient || undefined,
        qMaster: qMaster || undefined,
        qAuthor: qAuthor || undefined,
        qTime: qTime || undefined,
        qStatus: qStatus || undefined,
        qPrice: qPrice || undefined,
        qForecast: qForecast || undefined,
        qCreated: qCreated || undefined,
        createdFrom: createdFrom || undefined,
        createdTo: createdTo || undefined,
        withTotal,
        sort,
        sortDir,
      }),
    staleTime: QUERY_STALE_MS,
    placeholderData: withTotal ? keepPreviousData : undefined,
  })
}

export function useCurrentRecord(recordId) {
  return useQuery({
    queryKey: ["record", recordId],
    queryFn: () => recordService.getRecordById(recordId),
    enabled: Boolean(recordId),
    staleTime: QUERY_STALE_MS,
  })
}

/** CRM calendar board: batch availability for visible days. */
export function useCrmAvailability(
  userId,
  masterId,
  boardDates,
  { duration, serviceId, selectedSlots } = {},
  { enabled = true } = {}
) {
  return useQuery({
    queryKey: [
      "availability",
      "crm",
      userId,
      masterId,
      boardDates,
      duration,
      serviceId,
      selectedSlots?.length,
    ],
    queryFn: ({ signal }) => {
      const batchOptions = { signal }
      if (serviceId) {
        batchOptions.serviceId = serviceId
      }
      if (duration) {
        batchOptions.duration = duration
      }
      if (selectedSlots?.length) {
        batchOptions.selectedSlots = selectedSlots
      }
      return recordService.getAvailableCrmRecordsBatch(
        userId,
        masterId,
        boardDates,
        batchOptions
      )
    },
    enabled: enabled && Boolean(userId) && Boolean(masterId) && boardDates?.length > 0,
    staleTime: 0,
  })
}

/** Client calendar board: batch availability for visible days. */
export function useClientAvailability(
  masterId,
  boardDates,
  { duration, serviceId, selectedSlots } = {},
  { enabled = true } = {}
) {
  return useQuery({
    queryKey: [
      ...clientCompanyQueryPrefix(),
      "availability",
      "client",
      masterId,
      boardDates,
      duration,
      serviceId,
      selectedSlots?.length,
    ],
    queryFn: ({ signal }) => {
      const batchOptions = { signal }
      if (serviceId) {
        batchOptions.serviceId = serviceId
      }
      if (duration) {
        batchOptions.duration = duration
      }
      if (selectedSlots?.length) {
        batchOptions.selectedSlots = selectedSlots
      }
      return recordService.getAvailableRecordsBatch(
        masterId,
        boardDates,
        batchOptions
      )
    },
    enabled: enabled && Boolean(masterId) && boardDates?.length > 0,
    staleTime: 0,
  })
}

async function prefetchCatalogQueries(queryClient) {
  const prefix = clientCompanyQueryPrefix()
  await Promise.all([
    queryClient.prefetchQuery({
      queryKey: [...prefix, "users", USERS_LIMIT, USERS_OFFSET],
      queryFn: () =>
        userService.getUsers({ limit: USERS_LIMIT, offset: USERS_OFFSET }),
      staleTime: QUERY_STALE_MS,
    }),
    queryClient.prefetchQuery({
      queryKey: [...prefix, "services"],
      queryFn: () => serviceService.getServices(),
      staleTime: QUERY_STALE_MS,
    }),
    queryClient.prefetchQuery({
      queryKey: [...prefix, "complexes"],
      queryFn: () => serviceService.getComplexes(),
      staleTime: QUERY_STALE_MS,
    }),
  ])
}

export async function prefetchCrmData(queryClient, userId) {
  await Promise.all([
    prefetchCatalogQueries(queryClient),
    queryClient.prefetchQuery({
      queryKey: ["user", userId],
      queryFn: () => userService.getUserById(userId),
      staleTime: QUERY_STALE_MS,
    }),
    queryClient.prefetchQuery({
      queryKey: ["clients", CLIENTS_LOAD_LIMIT, 0],
      queryFn: () =>
        clientService.getClients({ limit: CLIENTS_LOAD_LIMIT, offset: 0 }),
      staleTime: QUERY_STALE_MS,
    }),
    queryClient.prefetchQuery({
      queryKey: ["comments", COMMENTS_LOAD_LIMIT, 0],
      queryFn: () =>
        commentService.getComments({ limit: COMMENTS_LOAD_LIMIT, offset: 0 }),
      staleTime: QUERY_STALE_MS,
    }),
    queryClient.prefetchQuery({
      queryKey: ["records", "user", userId, RECORDS_LOAD_LIMIT],
      queryFn: () =>
        recordService.getRecords({
          userId: Number(userId),
          limit: RECORDS_LOAD_LIMIT,
        }),
      staleTime: QUERY_STALE_MS,
    }),
  ])

  const user = queryClient.getQueryData(["user", userId])
  const profile = pickUserProfile(user)
  if (profile) {
    localStorageService.setUserProfile(profile)
  }
  return user
}

/** @deprecated use prefetchCrmData */
export const prefetchCalendarData = prefetchCrmData

export async function prefetchClientData(queryClient, clientId) {
  const prefix = clientCompanyQueryPrefix()
  await Promise.all([
    prefetchCatalogQueries(queryClient),
    queryClient.prefetchQuery({
      queryKey: [...prefix, "client", clientId],
      queryFn: () => clientService.getClientById(clientId),
      staleTime: QUERY_STALE_MS,
    }),
  ])

  const client = queryClient.getQueryData([...prefix, "client", clientId])
  const profile = pickClientProfile(client)
  if (profile) {
    localStorageService.setClientProfile(profile)
  }
  return client
}
